import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Modal, Loader, Text, TextInput } from '@simplybenefits/sb-ui-library';
import Grid from "@mui/material/Grid";
import ManualTransactionBlock from '../../components/ManualTransactionBlock.js';
import { getPendingManualTransactions, setNeedsMoreInformation, removePendingDentalClaim } from '../../../../actions/adjudication.js';
import permissionLevel from '../../../../services/permissions.js';
import store from '../../../../redux/store.js';
import * as dates from "@simplybenefits/sb-dates";

class PendingManualTransaction extends PureComponent {
    state = {
        type: null, // claim, cob or predetermination
        query: null,
        transactions: null,
        showRemovePendingManualClaim: false,
        uidToRemove: null
    };

    static getDerivedStateFromProps(props) {
        if (!permissionLevel('Adjudication', props.master.access).includes('read')) {
            window.location.href = '/403';
        }

        return null;
    }

    componentDidMount() {
        this._getAllManualTransactions();
    }

    _handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            this._getAllManualTransactions();
        }

        return false;
    };

    _handleInputChange = async (type, e) => {
        if (e.target.value === 'All') {
            e.target.value = null;
        }

        await this.setState({
            [type]: e.target.value
        });

        if (type !== 'query') {
            await this._getAllManualTransactions();
        }
    };

    _getAllManualTransactions = async () => {
        const transactions = await this.props.dispatch(getPendingManualTransactions((this.state.type === 'First Payor' ? 'claim' : this.state.type), this.state.query));

        this.setState({
            transactions
        });
    };

    _setNeedsMoreInformation = async (uid) => {
        await this.props.dispatch(setNeedsMoreInformation(uid));

        this.setState({
            transactions: this.state.transactions.map((transaction) => {
                if (transaction.uid === uid) {
                    return {
                        ...transaction,
                        status: 'Incomplete'
                    };
                }

                return transaction;
            })
        });
    };

    _setReassignedClaim = async (ediClaimID, masterID) => {
        const adjudicator = store.getState().master.adjudicators.find((item) => item.masterID === masterID);
        
        await this.setState({
            transactions: this.state.transactions.map((transaction) => {
                if (transaction.ediClaimID === ediClaimID) {
                    return {
                        ...transaction,
                        assignedTo: `${ adjudicator.firstname } ${ adjudicator.lastname }`,
                        dateAssigned: dates.getUTC()
                    };
                }
                
                return transaction;
            })
        });
    };

    _handleDeletePendingClaim = async (uid, showModal = true) => {
        if (showModal) {
            this.setState({
                showRemovePendingManualClaim: true,
                uidToRemove: uid
            });

            return null;
        }

        const success = await this.props.dispatch(removePendingDentalClaim(this.state.uidToRemove));

        if (success) {
            // close the modal and unset the uid to remove
            this.setState({
                showRemovePendingManualClaim: false,
                uidToRemove: null,
                transactions: this.state.transactions.filter((item) => item.uid !== this.state.uidToRemove)
            });
        }

        return null;
    };

    render() {
        if (!this.state.transactions || this.props.master.loading) {
            return <Loader isContainer/>;
        }

        return (
            <>
                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    style={{
                        marginBottom: 20
                    }}
                >
                    <Grid item xs={12} md={6} lg={8} style={{ paddingLeft: 5, paddingRight: 5 }} onKeyPress={ (e) => this._handleKeyPress(e) }>
                        <TextInput
                            label="Search By Certificate #"
                            fullWidth
                            value={ this.state.query || '' }
                            variant="outlined"
                            onChange={(e) => this._handleInputChange("query", e)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={4} style={{ paddingLeft: 5, paddingRight: 5 }}>
                        <TextInput
                            label="Type"
                            fullWidth
                            value={ this.state.type || 'All' }
                            variant="outlined"
                            onChange={(e) => this._handleInputChange("type", e)}
                            select
                            selectOptions={[
                                'All',
                                'First Payor',
                                'COB',
                                'Predetermination'
                            ]}
                        />
                    </Grid>
                </Grid>

                <ManualTransactionBlock isHeader access={ this.props.master.access } />

                {
                    this.state.transactions.map((transaction) => ( 
                        <ManualTransactionBlock
                            key={transaction.uid}
                            ediClaimID={ transaction.ediClaimID }
                            uid={transaction.uid}
                            type={transaction.type}
                            status={transaction.status}
                            isDependent={transaction.isDependent}
                            payee={transaction.payee}
                            patient={transaction.patient}
                            numProcedures={transaction.numProcedures}
                            submissionDate={transaction.dateAdded}
                            assignedTo={ transaction.assignedTo }
                            dateAssigned={ transaction.dateAssigned }
                            access={ this.props.master.access }
                            setNeedsMoreInformation={ () => this._setNeedsMoreInformation(transaction.uid) }
                            handleDeletePendingClaim={ () => this._handleDeletePendingClaim(transaction.uid) }
                            setReassignedClaim={ (ediClaimID, masterID) => this._setReassignedClaim(ediClaimID, masterID)}
                        />
                    ))
                }

                {
                    this.state.transactions.length === 0 &&
                    <Text
                        align="center"
                        mediumLarge
                        display="block"
                    >
                        No pending transactions to display.
                    </Text>
                }

                <Modal
                    open={this.state.showRemovePendingManualClaim}
                    onClose={() => {
                        this.setState({
                            showRemovePendingManualClaim: false,
                            uidToRemove: null
                        });
                    }}
                    title="Remove Pending Claim"
                >
                    <>
                        <Text
                            align="left"
                            medium
                            display="inline-block"
                            margin="10px 0"
                        >
                            A claim removal cannot be undone. Are you sure you want to continue?
                        </Text>

                        <Button
                            onClick={ () => this._handleDeletePendingClaim(null, false) }
                            marginTop={2.5}
                            color="primary"
                            fullWidth
                        >
                            Remove Claim
                        </Button>
                    </>
                </Modal>

            </>
        );
    }
}

PendingManualTransaction.propTypes = {
    master: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    masterLoading: state.master.loading,
    master: state.master
});

export default connect(mapStateToProps)(PendingManualTransaction);